// src/pages/News/EditNews.tsx

import {
  AddTwoTone,
  AnnouncementTwoTone,
  CalendarTodayTwoTone,
  CategoryTwoTone,
  DeleteTwoTone,
  FaceTwoTone,
  HomeWorkTwoTone,
  MoreVertTwoTone,
  SaveTwoTone,
  TitleTwoTone,
  ViewHeadlineTwoTone,
} from '@mui/icons-material'
import { Avatar, IconButton, MenuItem } from '@mui/material'
import { useEffect, useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'

import { ContractPool } from '@/types/domain/contract'
import { FolderPool } from '@/types/domain/folder'

import { AM, AMItem, KPage, KSelect, KTextField } from '../../components'
import { useContractPool, useFolderPool, useNewsApi } from '../../hooks'

export const EditNews = () => {
  const navigate = useNavigate()
  const { id } = useParams()

  const editMode = id ? true : false

  const cPool: ContractPool = useContractPool()
  const fPool: FolderPool = useFolderPool()

  const [newsTypes, setNewsTypes] = useState<any[]>([])
  const [newsData, setNewsData] = useState<any | null>(null)

  const {
    getById: getNewsById,
    getTypes: getNewsTypes,
    remove: removeNews,
    save: saveNews,
    update: updateNews,
  } = useNewsApi()

  useEffect(() => {
    const fetchTypes = async () => {
      const data = await getNewsTypes()
      setNewsTypes(data)
    }

    fetchTypes()
  }, [getNewsTypes])

  useEffect(() => {
    const fetchNews = async () => {
      const data = await getNewsById(Number(id))
      setNewsData(data)
    }

    fetchNews()
  }, [getNewsById, id])

  const [actionMenu, setActionMenu] = useState(null)

  const [title, setTitle] = useState('')
  const [headline, setHeadline] = useState('')
  const [author, setAuthor] = useState('')
  const [content_text, setContent_text] = useState('')
  const [start_time, setStart_time] = useState('')

  const [news_type_id, setNews_type_id] = useState<number | null>(null)
  const [news_type_idSelect, setNews_type_idSelect] = useState(null)

  const [folder, setFolder] = useState<{ value: string; label: string } | null>(
    null
  )
  const [contract, setContract] = useState<{
    value: string
    label: string
  } | null>(null)

  useEffect(() => {
    if (editMode && newsData) {
      const {
        author,
        content_text,
        contracts,
        folder_id,
        headline,
        label,
        news_type_id,
        start_time,
        title,
      } = newsData

      setTitle(title)
      setHeadline(headline)
      setAuthor(author)
      setContent_text(content_text)
      setStart_time(start_time)
      setNews_type_id(Number(news_type_id))
      setNews_type_idSelect({
        value: Number(news_type_id),
        label,
      })

      if (folder_id) {
        setFolder(
          fPool
            .filter(f => f.id === String(folder_id))
            .map(item => {
              const { name, name_short } = item.attributes

              return {
                value: item.id,
                label: name + (name_short ? ' (' + name_short + ')' : ''),
              }
            })[0] || null
        )
      }

      if (contracts) {
        setContract(
          contracts.map(contract => ({
            value: String(contract.entity_id),
            label: contract.name,
          }))[0]
        )
      }
    }
  }, [newsData, fPool, editMode])

  const onSubmit = action => {
    const record = {
      author,
      content_text,
      contract_id: contract
        ? [{ value: String(contract.value), label: contract.label }]
        : [],
      folder_id: folder ? Number(folder.value) : undefined,
      headline,
      news_type_id: news_type_id ?? undefined,
      start_time,
      title,
    }

    switch (action) {
      case 'save':
        saveNews(record)
        break

      case 'update':
        updateNews(Number(id), record)
        break

      case 'delete':
        removeNews(Number(id))
        navigate('/news')
        break

      default:
        console.error('unhandled action', action)
        break
    }
  }

  return (
    <>
      {/* action menu */}
      <AM
        anchorEl={actionMenu}
        caption={'News'}
        onClose={() => setActionMenu(null)}
        open={Boolean(actionMenu)}
        historyUrlTarget={'news'}
      >
        <AMItem
          caption={'Neu'}
          icon={<AddTwoTone />}
          onClick={() => navigate('/news/new')}
        />

        {editMode && (
          <AMItem
            caption={'Speichern'}
            icon={<SaveTwoTone />}
            onClick={() => onSubmit('update')}
          />
        )}

        <AMItem
          disabled={!editMode}
          caption={'Löschen'}
          icon={<DeleteTwoTone />}
          onClick={() => onSubmit('delete')}
        />

        {!editMode && (
          <AMItem
            caption={'Speichern'}
            icon={<SaveTwoTone />}
            onClick={() => onSubmit('save')}
          />
        )}
      </AM>
      {/* action menu end */}

      <KPage
        action={
          <IconButton onClick={event => setActionMenu(event.currentTarget)}>
            <MoreVertTwoTone />
          </IconButton>
        }
        avatar={
          <Avatar>
            <AnnouncementTwoTone />
          </Avatar>
        }
        title={'News bearbeiten'}
        subheader={title === '' ? 'Neue News' : title}
      >
        {/* Titel */}
        <KTextField
          error={!title || title === ''}
          icon={<TitleTwoTone />}
          label={'Titel'}
          onChange={event => setTitle(event.target.value)}
          required
          value={title || ''}
        />
        {/* Headline */}
        <KTextField
          icon={<TitleTwoTone />}
          label={'Headline'}
          onChange={event => setHeadline(event.target.value)}
          value={headline || ''}
        />
        {/* Rubrik */}
        <KSelect
          icon={<CategoryTwoTone />}
          label={'Rubrik'}
          onChange={event => {
            const value = Number(event.target.value)
            const item = newsTypes.find(n => Number(n.id) === value)

            if (item) {
              setNews_type_id(item.id)
              setNews_type_idSelect({
                value: item.id,
                label: item.attributes.name,
              })
            } else {
              setNews_type_id(null)
              setNews_type_idSelect(null)
            }
          }}
          value={(news_type_idSelect && news_type_idSelect.value) || ''}
        >
          {newsTypes.map(item => {
            const { name } = item.attributes

            return (
              <MenuItem key={item.id} value={item.id}>
                {name}
              </MenuItem>
            )
          })}
        </KSelect>
        {/* Author */}
        <KTextField
          icon={<FaceTwoTone />}
          label={'Autor'}
          onChange={event => setAuthor(event.target.value)}
          value={author || ''}
        />
        {/* Release */}
        <KTextField
          icon={<CalendarTodayTwoTone />}
          label={'Release'}
          onChange={event => setStart_time(event.target.value)}
          type={'datetime-local'}
          value={start_time || ''}
        />
        {/* Inhalt */}
        <KTextField
          icon={<ViewHeadlineTwoTone />}
          label={'Inhalt'}
          maxRows={30}
          multiline
          onChange={event => setContent_text(event.target.value)}
          value={content_text}
        />
        {/* Gebäude */}
        <KSelect
          icon={<HomeWorkTwoTone />}
          label={'Gebäude'}
          onChange={event => {
            const value = String(event.target.value)
            const item = fPool.find(f => String(f.id) === value)

            setFolder(
              item
                ? {
                    value: item.id,
                    label:
                      item.attributes.name +
                      (item.attributes.name_short
                        ? ` (${item.attributes.name_short})`
                        : ''),
                  }
                : null
            )
          }}
          value={(folder && folder.value) || ''}
        >
          <MenuItem value={''}>Bitte wählen ...</MenuItem>
          {fPool.map(item => {
            const { name, name_short } = item.attributes

            return (
              <MenuItem key={item.id} value={item.id}>
                {name} {name_short && ` (${name_short})`}
              </MenuItem>
            )
          })}
        </KSelect>
        {/* Vertrag */}
        <KSelect
          icon={<HomeWorkTwoTone />}
          label={'Vertrag'}
          onChange={event =>
            setContract({
              value: String(event.target.value),
              label:
                cPool.find(c => Number(c.id) === Number(event.target.value))
                  ?.attributes.name ?? '',
            })
          }
          value={contract ? contract.value : ''}
        >
          <MenuItem value={''}>Bitte wählen ...</MenuItem>
          {cPool.map(item => {
            const { name } = item.attributes

            return (
              <MenuItem key={item.id} value={item.id}>
                {name}
              </MenuItem>
            )
          })}
        </KSelect>
      </KPage>
    </>
  )
}
